import { track } from "@/utils/events/tracker";
import axios from "axios";
import { toast } from "sonner";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useDispatch } from "react-redux";
import { JsxElement } from "@/utils/jsx/jsxElementTypes";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  logAxiosError,
  useActiveCanvas,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCallback, useEffect, useRef } from "react";
import { sanitizeComponentName } from "@/components/AppHeader/utils";
import { useSession } from "@clerk/clerk-react";
import { NavTreeNode, Storyboard } from "@/utils/types";
import useActivityStore from "@/store/zustand/activityStore";
import { shouldUseAiGenerationSafe } from "@/utils/aiGenerationUtils";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { processInstantUpdateData } from "@/utils/navTreeUtils";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import {
  fixUniquePathsForAllChildren,
  updateAllStoreLookups,
} from "@/components/Canvas/Storyboard/StaticIframe/utils/navTreeLookupUtils";
import {
  StoryboardDelta,
  UpdateStoryboardsChange,
  WrapStoryboardsChange,
} from "@/store/zustand/changeLedgerTypes";
import { useTextEditStore } from "@/components/Canvas/Storyboard/StaticIframe/hooks/textEditStore";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useInitializeStoryboard } from "./useInitializeStoryboards";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { getContentsWithoutShell } from "@/components/Canvas/IframePool/utils";
import { wrapNodesInDiv } from "@/components/Canvas/Storyboard/StaticIframe/utils/applyWrapInDiv";
import _ from "lodash";
import { TempoElement } from "@/utils/tempoElement";
import { v4 as uuidv4 } from "uuid";
import { generateStoryboardFilename } from "@/utils/fileNameUtils";

interface WrapStoryboardsParams {
  sourceStoryboards: Storyboard[];
}

export const useWrapStoryboards = () => {
  const canvasId = useCanvasId();

  const project = useActiveProject();
  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const canvas = useActiveCanvas();
  const canvasRef = useRef(canvas);
  useEffect(() => {
    canvasRef.current = canvas;
  }, [canvas]);

  const initializeStoryboard = useInitializeStoryboard();

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  return useCallback(
    async ({
      sourceStoryboards,
    }: WrapStoryboardsParams): Promise<Storyboard | null> => {
      if (!supabaseClient) {
        console.error("Supabase client not available");
        return null;
      }

      if (!canvasRef.current) {
        console.error("Canvas not available");
        return null;
      }

      // Gather source files from the storyboards
      const sourceFiles: { fileContents: string; filename: string }[] = [];
      const sourceHtmls: string[] = [];
      const sourceNavTrees: NavTreeNode[] = [];

      const fileToContents = useCodebaseStore.getState().fileToContents;
      const storyboardToNavTree = useCanvasStore.getState().storyboardToNavTree;
      const htmlForStoryboard = useIframePoolStore.getState().htmlForStoryboard;

      let totalWidth = 0;
      let totalHeight = 0;
      let minX = Infinity;
      let minY = Infinity;
      let maxZ = -Infinity;
      for (const storyboard of sourceStoryboards) {
        if (storyboard?.fullFilePath) {
          const fileContents = fileToContents?.[storyboard.fullFilePath];

          if (!fileContents) {
            toast.error(`No file contents found for ${storyboard.name}`);
            return null;
          }

          sourceFiles.push({
            fileContents,
            filename: storyboard.fullFilePath,
          });

          if (!storyboardToNavTree[storyboard.id]) {
            toast.error(`No nav tree found for ${storyboard.name}`);
            return null;
          }

          sourceNavTrees.push(_.cloneDeep(storyboardToNavTree[storyboard.id]));

          if (!htmlForStoryboard[storyboard.id]) {
            toast.error(`No html found for ${storyboard.name}`);
            return null;
          }

          sourceHtmls.push(
            getContentsWithoutShell(htmlForStoryboard[storyboard.id]),
          );

          // TODO: Adjust after we make changes to the layout that gets chosen for the wrap
          // For now it is just block so the elements are stacked vertically
          totalWidth = Math.max(totalWidth, storyboard.width);
          totalHeight += storyboard.height;

          minX = Math.min(minX, storyboard.x);
          minY = Math.min(minY, storyboard.y);
          maxZ = Math.max(maxZ, storyboard.zIndex);
        }
      }

      const newStoryboard = initializeStoryboard(
        minX,
        minY,
        totalWidth,
        totalHeight,
      );

      // Get page info for deltas
      const pagesStore = usePagesStore.getState();
      const selectedPageId = pagesStore.selectedPageId[canvasId];
      const pageConfigPath = selectedPageId
        ? pagesStore.getPageSourcePath(canvasId, selectedPageId)
        : undefined;

      const deltas: StoryboardDelta[] = [
        {
          changeType: "upsert",
          storyboardId: newStoryboard.id,
          pageId: selectedPageId || "",
          pageConfigPath,
          storyboardUpdateDict: newStoryboard,
        },
      ];

      // Then delete the wrapped storyboards
      for (const storyboard of sourceStoryboards) {
        const sourcePageConfigPath = storyboard.pageId
          ? pagesStore.getPageSourcePath(canvasId, storyboard.pageId)
          : pageConfigPath;
        deltas.push({
          changeType: "remove",
          storyboardId: storyboard.id,
          pageId: storyboard.pageId || "",
          pageConfigPath: sourcePageConfigPath,
          storyboardUpdateDict: {},
        });
      }

      const expectedFilename = useCodebaseStore
        .getState()
        .getExpectedFilenameForStoryboard(newStoryboard.id);

      // Generate a new ID for the wrap div
      const wrapDivCodebaseId = uuidv4();

      const classes = [`w-[${totalWidth}px]`, `h-[${totalHeight}px]`];
      const mergedHtml = `<div class="${classes.join(
        " ",
      )}" data-tempoelementid="${wrapDivCodebaseId}" style="width: ${totalWidth}px; height: ${totalHeight}px;">${sourceHtmls.join(
        "",
      )}</div>`;
      const mergedNavTree = wrapNodesInDiv(
        sourceNavTrees,
        newStoryboard.id,
        classes,
        wrapDivCodebaseId,
      );
      fixUniquePathsForAllChildren(mergedNavTree);

      const {
        html: htmlForInstantUpdate,
        navTree: navTreeForInstantUpdate,
        error,
      } = processInstantUpdateData({
        storyboardId: newStoryboard.id,
        canvasId,
        projectSubfolderPath: projectRef.current?.subfolderPath,
        instantUpdateData: {
          html: mergedHtml,
          navTree: mergedNavTree,
        },
        wrapInShell: true,
      });

      if (error) {
        toast.error(error);
        return null;
      }

      // Compute destination file path for new readable filename format
      let destinationFilePath: string | undefined;
      if (pageConfigPath) {
        const pageFolder = pageConfigPath.substring(
          0,
          pageConfigPath.lastIndexOf("/"),
        );
        const extension = ".tsx"; // TODO: Get from project config if needed
        const filename = generateStoryboardFilename(
          newStoryboard.name || "WrappedStoryboard",
          newStoryboard.id,
          extension,
        );
        destinationFilePath = `${pageFolder}/${filename}`;
      }

      const wrapChange = new WrapStoryboardsChange({
        storyboardName: newStoryboard.name || "WrappedStoryboard",
        newStoryboardId: newStoryboard.id,
        canvasId,
        wrapDivClasses: `w-[${totalWidth}px] h-[${totalHeight}px]`, // Default wrapping classes
        sourceFiles,
        deltas,
        navTreeForInstantUpdate,
        htmlForInstantUpdate,
        pageConfigPath: pageConfigPath || "",
        destinationFilePath,
      });

      useDesignCommandManager.getState().executeCommand(wrapChange);

      wrapChange.setSelectedElementsAfterInstantUpdate(
        TempoElement.forStoryboard(newStoryboard.id).getKey(),
        [],
      );

      const getComponentDataFromRes = (
        data?: any,
      ): {
        error?: string;
        fullFilePath?: string;
        componentCodebaseId?: string;
      } => {
        const treeElements = data?.treeElementLookup;
        if (!treeElements || !Object.keys(treeElements).length) {
          return {
            error: `Error creating storyboard, no main component found. Please make sure the code is correct for all the storyboard to wrap`,
          };
        }

        let createdComponent: any = Object.values(treeElements).find(
          (treeElement: any) =>
            treeElement.type === "component" &&
            treeElement.componentName === newStoryboard.name,
        );

        if (!createdComponent) {
          // Match the first component that is the default export
          createdComponent = Object.values(treeElements).find(
            (treeElement: any) =>
              treeElement.type === "component" && treeElement.defaultExport,
          );
        }

        const fullFilePath = createdComponent
          ? createdComponent.filename
          : // @ts-ignore
            Object.values(treeElements)[0].filename;

        if (!fullFilePath) {
          return { error: "Error creating storyboard: No file path found" };
        }

        return {
          fullFilePath,
          componentCodebaseId: createdComponent?.codebaseId || null,
        };
      };

      wrapChange.setResponseValidator((data) => {
        const { error } = getComponentDataFromRes(data);
        if (error) {
          return error;
        }
        return null;
      });

      try {
        const data = await useDesignCommandManager
          .getState()
          .executeCommandAsPromise(wrapChange);

        const { fullFilePath, componentCodebaseId } =
          getComponentDataFromRes(data);

        const storyboardUpdateDict = {
          created: true,
          created_at: new Date().toISOString(),
          fullFilePath,

          // If instant update, no need to wait for the server to be ready
          createdHotReloadTs: data.wasInstantUpdate
            ? undefined
            : data.hotReloadTs,
        };

        const storyboardDeltas: StoryboardDelta[] = [
          {
            changeType: "update",
            storyboardId: newStoryboard.id,
            pageId: selectedPageId || "",
            pageConfigPath,
            storyboardUpdateDict: storyboardUpdateDict,
          },
        ];

        const fullStoryboardDict = {
          ...newStoryboard,
          ...storyboardUpdateDict,
        };

        // Update the delta on the change so undo/redo works
        wrapChange.changeFields.deltas[0].storyboardUpdateDict =
          fullStoryboardDict;

        await updateCanvasStoryboards(
          wrapChange.changeFields.canvasId,
          storyboardDeltas,
        );

        return fullStoryboardDict;
      } catch (error) {
        console.error("Error creating storyboard:", error);
        toast.error("Error creating storyboard");
        return null;
      }
    },
    [supabaseClient, initializeStoryboard, canvasId],
  );
};
